/**
 * Telegram Mini App SDK типы и утилиты
 */

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp
    }
    TelegramWebviewProxy?: {
      postEvent?: (eventType: string, eventData?: string) => void
    }
  }
}

const TELEGRAM_WEB_TARGET_ORIGIN = 'https://web.telegram.org'

export interface TelegramPopupButton {
  id?: string
  type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive'
  text?: string
}

export interface TelegramPopupParams {
  title?: string
  message: string
  buttons?: TelegramPopupButton[]
}

export interface TelegramSafeAreaInset {
  top?: number
  right?: number
  bottom?: number
  left?: number
}

export interface TelegramWebApp {
  ready: () => void
  expand: () => void
  close: () => void
  sendData: (data: string) => void
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void
  openTelegramLink: (url: string) => void
  showPopup?: (params: TelegramPopupParams, callback?: (buttonId: string) => void) => void
  initData: string
  initDataUnsafe: {
    query_id?: string
    user?: TelegramUser
    auth_date?: number
    hash?: string
    start_param?: string
  }
  MainButton: MainButton
  HapticFeedback: HapticFeedback
  BackButton: BackButton
  SettingsButton: SettingsButton
  onEvent: (eventType: string, callback: () => void) => void
  offEvent: (eventType: string, callback: () => void) => void
  isVersionAtLeast: (version: string) => boolean
  colorScheme: 'light' | 'dark'
  themeParams: ThemeParams
  viewportHeight: number
  viewportStableHeight: number
  isExpanded: boolean
  isVerticalSwipesEnabled?: boolean
  isFullscreen?: boolean
  safeAreaInset?: TelegramSafeAreaInset
  contentSafeAreaInset?: TelegramSafeAreaInset
  headerColor: string
  backgroundColor: string
  setHeader?: (params: { header_color?: string; text_color?: string }) => void
  lockOrientation?: (orientation: 'portrait' | 'landscape') => void
  enableVerticalSwipes?: () => void
  disableVerticalSwipes?: () => void
  requestFullscreen?: () => void
  exitFullscreen?: () => void
  TextInput: TextInput
}

export interface TelegramUser {
  id: number
  is_bot: boolean
  first_name: string
  last_name?: string
  username?: string
  language_code?: string
  is_premium?: boolean
  added_to_attachment_menu?: boolean
  allows_write_to_pm?: boolean
  photo_url?: string
}

export interface MainButton {
  text: string
  color: string
  textColor: string
  isVisible: boolean
  isActive: boolean
  isLoading: boolean
  setText: (text: string) => void
  onClick: (callback: () => void) => void
  show: () => void
  hide: () => void
  enable: () => void
  disable: () => void
  showProgress: (leaveActive?: boolean) => void
  hideProgress: () => void
  setParams: (params: Partial<MainButton>) => void
}

export interface HapticFeedback {
  impactOccurred: (style: 'light' | 'medium' | 'heavy') => void
  notificationOccurred: (type: 'error' | 'success' | 'warning') => void
  selectionChanged: () => void
}

export interface BackButton {
  isVisible: boolean
  onClick: (callback: () => void) => void
  offClick?: (callback: () => void) => void
  show: () => void
  hide: () => void
}

export interface SettingsButton {
  isVisible: boolean
  onClick: (callback: () => void) => void
  show: () => void
  hide: () => void
}

export interface TextInput {
  isVisible: boolean
  placeholder: string
  value: string
  isValueChangeEventEnabled: boolean
  setParams: (params: Partial<TextInput>) => void
  focus: () => void
  blur: () => void
  show: () => void
  hide: () => void
}

export interface ThemeParams {
  bg_color: string
  text_color: string
  hint_color: string
  link_color: string
  button_color: string
  button_text_color: string
  secondary_bg_color: string
  accent_text_color?: string
  destructive_text_color?: string
  section_bg_color?: string
  section_header_text_color?: string
  subtitle_text_color?: string
  placeholder_text_color?: string
  border_color?: string
  keyboard_bg_color?: string
  keyboard_text_color?: string
  input_border_color?: string
}

export const getTelegramWebApp = (): TelegramWebApp | null => {
  if (typeof window === 'undefined') return null
  const webApp = window.Telegram?.WebApp
  return webApp || null
}

export const useTelegramWebApp = getTelegramWebApp

export const postTelegramEvent = (eventType: string, eventData?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') {
    return
  }

  const payload = eventData ?? {}

  if (window.TelegramWebviewProxy?.postEvent) {
    window.TelegramWebviewProxy.postEvent(eventType, JSON.stringify(payload))
    return
  }

  if (window.parent && window.parent !== window && typeof window.parent.postMessage === 'function') {
    window.parent.postMessage(
      JSON.stringify({
        eventType,
        eventData: payload,
      }),
      TELEGRAM_WEB_TARGET_ORIGIN,
    )
    return
  }

  const legacyExternalBridge = window.external as unknown as { notify?: (message: string) => void } | undefined
  if (legacyExternalBridge?.notify) {
    legacyExternalBridge.notify(
      JSON.stringify({
        eventType,
        eventData: payload,
      }),
    )
  }
}

export const expandTelegramApp = (webApp: TelegramWebApp | null = getTelegramWebApp()): void => {
  try {
    webApp?.expand?.()
  } catch {
    // no-op
  }

  postTelegramEvent('web_app_expand')
}

export const requestTelegramFullscreen = (webApp: TelegramWebApp | null = getTelegramWebApp()): void => {
  try {
    webApp?.requestFullscreen?.()
  } catch {
    // no-op
  }

  postTelegramEvent('web_app_request_fullscreen')
}

export const exitTelegramFullscreen = (webApp: TelegramWebApp | null = getTelegramWebApp()): void => {
  try {
    webApp?.exitFullscreen?.()
  } catch {
    // no-op
  }

  postTelegramEvent('web_app_exit_fullscreen')
}

export const initializeTelegramApp = (): void => {
  const webApp = getTelegramWebApp()
  if (webApp) {
    expandTelegramApp(webApp)
    requestTelegramFullscreen(webApp)
    webApp.ready()
  }
}

export const getCurrentUser = (): TelegramUser | null => {
  const webApp = getTelegramWebApp()
  return webApp?.initDataUnsafe?.user || null
}
